import { Disclosure, DisclosureContent, DisclosureProvider } from '@ariakit/react'
import { useState } from 'react'

import { Carat, RotateClockwise } from '@syconium/little-miss-figgy'

import {
  OptionDisclosureBody,
  OptionDisclosureShell,
  OptionDisclosureSummary,
  OptionDisclosureToggle,
  OptionRowLabel,
  OptionRowSubLabel,
  OptionRowText,
  OptionRowTrailing,
} from './styles'

export type PanelOptionDisclosureProps = {
  label: string
  subLabel?: string
  /** Shown under the header only while the row is closed, e.g. the field's error. */
  closedSummary?: React.ReactNode
  /** What the row opens to. */
  children: React.ReactNode
}

/**
 * A row of the options panel that opens in place instead of opening an editor over the list —
 * how the garment's color and size are changed without leaving the options.
 *
 * @link https://ariakit.org/reference/disclosure
 */
export const PanelOptionDisclosure: React.FC<PanelOptionDisclosureProps> = ({
  label,
  subLabel,
  closedSummary,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <DisclosureProvider open={isOpen} setOpen={setIsOpen}>
      <OptionDisclosureShell>
        <Disclosure render={<OptionDisclosureToggle type='button' />}>
          <OptionRowText>
            <OptionRowLabel>{label}</OptionRowLabel>
            {subLabel !== undefined && <OptionRowSubLabel>{subLabel}</OptionRowSubLabel>}
          </OptionRowText>
          <OptionRowTrailing>
            <Carat
              rotateClockwise={isOpen ? RotateClockwise.ThreeQuarters : undefined}
              height='12px'
              width='12px'
              strokeWidth='1.1'
            />
          </OptionRowTrailing>
        </Disclosure>

        {!isOpen && closedSummary !== undefined && (
          <OptionDisclosureSummary>{closedSummary}</OptionDisclosureSummary>
        )}

        <DisclosureContent render={<OptionDisclosureBody />}>{children}</DisclosureContent>
      </OptionDisclosureShell>
    </DisclosureProvider>
  )
}
